// Market Opportunity Score — a country-level "where to launch / localize next"
// signal for the app currently being compared (issue #118).
//
// This is a PURE, derived, client-side index. It never fetches anything: every
// input is read from the per-country RegionRow data the comparison already
// loaded. There is no backend, no account, and no new network call.
//
// ── Mapping to the five signals named in the issue ────────────────────────────
// The issue asks for a national opportunity index blending:
//   竞品密度 competitor density · 榜单波动 chart volatility · 评论量 review volume
//   · 本地化缺口 localization gap · 增长动能 growth momentum
//
// We map each to the most honest proxy the loaded data actually supports, and
// gracefully drop any factor whose underlying data is absent (the weights of the
// remaining factors are re-normalized, so a first-visit static build still
// produces a meaningful score from always-available fields):
//
//   评论量  review volume  → userRatingCount (log-scaled vs the app's own max).
//                            Proven, engaged audience = stronger localize signal.
//   本地化缺口 localization → missing storefront language when Apple's
//                            languageCodesISO2A is present; otherwise
//                            languageCount as a coarse headroom proxy.
//   增长动能 growth momentum → popularityDelta vs the last saved snapshot when a
//                            prior visit exists; otherwise update recency
//                            (releaseDate). Both are per-storefront.
//   榜单波动 chart volatility → magnitude of the popularity swing across visits
//                            (|popularityDelta|). Active only once a prior
//                            snapshot exists.
//   竞品density competitor   → the app's OWN published chart standing
//                            (bestChartRank) as a visibility proxy. Active only
//                            when the optional RSS chart enrichment is enabled.
//                            A true competitor-density measure would need
//                            category chart data the static build does not fetch,
//                            so we do NOT fabricate it — consistent with the
//                            project's "no fake data" principle.
//
// Honesty note: this is a heuristic to help prioritize, not a guarantee of
// market success. It is built only from Apple's public signals (rating count,
// languageCodesISO2A/language count, release date, and — where enabled —
// published chart rank). It is never a download or revenue estimate.

import { bestChartRank } from './chartRank.js';
import { detectLocalizationGap } from './localizationGap.js';

/**
 * Tunable reference constants for the factor curves. Exported so tests and any
 * future settings UI can reason about the scale.
 */
export const OPPORTUNITY_PARAMS = Object.freeze({
  // languageCount at/above which the localization gap is considered closed.
  langFullReference: 15,
  // An update older than this many days scores 0 on the recency momentum proxy.
  recencyWindowDays: 365,
  // A popularity-score delta of ±this maps momentum to the 0/1 extremes.
  momentumDeltaScale: 40,
  // A |popularity delta| of this maps volatility to 1.0.
  volatilityDeltaScale: 40,
});

/**
 * Base factor weights. Active factors are re-normalized over whichever subset
 * has data, so these are relative, not absolute (they happen to sum to 1).
 */
export const OPPORTUNITY_WEIGHTS = Object.freeze({
  demand: 0.3, // 评论量 review volume
  localization: 0.25, // 本地化缺口 localization gap
  momentum: 0.2, // 增长动能 growth momentum
  visibility: 0.15, // 竞品密度 → chart visibility proxy (chart enrichment only)
  volatility: 0.1, // 榜单波动 chart volatility (snapshot-based)
});

/**
 * @typedef {object} OpportunityFactors
 * @property {number|null} demand        - 0..1 review-volume factor, or null if inactive.
 * @property {number|null} localization  - 0..1 localization-gap factor, or null.
 * @property {number|null} momentum      - 0..1 growth-momentum factor, or null.
 * @property {number|null} visibility    - 0..1 chart-visibility factor, or null.
 * @property {number|null} volatility    - 0..1 chart-volatility factor, or null.
 */

/**
 * @typedef {object} MarketOpportunity
 * @property {number} score                 - Composite 0..100 (rounded).
 * @property {OpportunityFactors} factors    - Per-factor 0..1 values (null = inactive).
 * @property {keyof OpportunityFactors|null} topFactor - Dominant active factor (largest weighted contribution), or null.
 */

/**
 * @typedef {object} OpportunityContext
 * @property {number} [maxRatingCount] - Max userRatingCount across available rows (for demand scaling).
 * @property {number} [nowMs]          - Reference "now" in epoch ms, for releaseDate recency. Injected for testability.
 */

/**
 * Score a single region row's market opportunity (0..100).
 *
 * Returns null when the row is unavailable/errored or when none of the factors
 * have any data to work with (nothing to base a score on).
 *
 * @param {import('./normalizeApp.js').RegionRow & import('./chartRank.js').RowChart & { popularityDelta?: {value: number}|null }} row
 * @param {OpportunityContext} [ctx]
 * @returns {MarketOpportunity|null}
 */
export function scoreMarketOpportunity(row, ctx = {}) {
  if (!row || row.available === false || row.error) return null;

  const factors = {
    demand: demandFactor(row, ctx.maxRatingCount),
    localization: localizationFactor(row),
    momentum: momentumFactor(row, ctx.nowMs),
    visibility: visibilityFactor(row),
    volatility: volatilityFactor(row),
  };

  // Collect the factors that actually have data and re-normalize their weights.
  let weightedSum = 0;
  let weightTotal = 0;
  let topFactor = null;
  let topContribution = -1;

  for (const key of Object.keys(OPPORTUNITY_WEIGHTS)) {
    const value = factors[key];
    if (value === null) continue;
    const weight = OPPORTUNITY_WEIGHTS[key];
    const contribution = weight * value;
    weightedSum += contribution;
    weightTotal += weight;
    if (contribution > topContribution) {
      topContribution = contribution;
      topFactor = key;
    }
  }

  if (weightTotal === 0) return null; // No factor had any data.

  const score = Math.round((weightedSum / weightTotal) * 100);
  return { score: clamp(score, 0, 100), factors, topFactor };
}

/**
 * Annotate each row with a `marketOpportunity` field (MarketOpportunity|null).
 * Rows are not reordered. `maxRatingCount` is derived from the row set so the
 * demand factor is scaled relative to this app's own audience.
 *
 * @param {Array<import('./normalizeApp.js').RegionRow>} rows
 * @param {OpportunityContext} [ctx]
 * @returns {Array<object>} New array; original rows are not mutated.
 */
export function annotateMarketOpportunity(rows, ctx = {}) {
  if (!Array.isArray(rows)) return [];
  const maxRatingCount = ctx.maxRatingCount ?? maxRatingCountOf(rows);
  const fullCtx = { ...ctx, maxRatingCount };
  return rows.map((row) => ({
    ...row,
    marketOpportunity: scoreMarketOpportunity(row, fullCtx),
  }));
}

/**
 * @typedef {object} OpportunityEntry
 * @property {number} rank          - 1-based rank within the returned list.
 * @property {string} countryCode
 * @property {string} countryName
 * @property {number} score         - Composite 0..100.
 * @property {keyof OpportunityFactors|null} topFactor - Dominant factor key.
 */

/**
 * Return the top-N countries by opportunity score (descending). Unavailable /
 * unscorable rows are excluded. Ties are broken by countryCode for stability.
 *
 * @param {Array<import('./normalizeApp.js').RegionRow>} rows
 * @param {OpportunityContext & { n?: number }} [options]
 * @returns {OpportunityEntry[]}
 */
export function topOpportunities(rows, options = {}) {
  const { n = 5, ...ctx } = options;
  const annotated = annotateMarketOpportunity(rows, ctx);
  return annotated
    .filter((r) => r.marketOpportunity !== null)
    .sort((a, b) => {
      const diff = b.marketOpportunity.score - a.marketOpportunity.score;
      if (diff !== 0) return diff;
      return String(a.countryCode).localeCompare(String(b.countryCode));
    })
    .slice(0, Math.max(0, n))
    .map((r, i) => ({
      rank: i + 1,
      countryCode: r.countryCode,
      countryName: r.countryName,
      score: r.marketOpportunity.score,
      topFactor: r.marketOpportunity.topFactor,
    }));
}

// ── Individual factor curves (each returns 0..1, or null when inactive) ───────

/**
 * Review volume (评论量): log-scaled userRatingCount relative to the app's own
 * cross-country maximum. Inactive when the row or the max has no rating count.
 */
function demandFactor(row, maxRatingCount) {
  const count = row.userRatingCount ?? null;
  if (count === null || count < 0) return null;
  if (!maxRatingCount || maxRatingCount <= 0) return null;
  const logCount = Math.log10(count + 1);
  const logMax = Math.log10(maxRatingCount + 1);
  if (logMax <= 0) return null;
  return clamp(logCount / logMax, 0, 1);
}

/**
 * Localization gap (本地化缺口): when Apple reports languageCodesISO2A, score
 * an explicit missing-storefront-language gap as 1 and a covered local language
 * as 0. Without that list, fall back to the older languageCount headroom proxy:
 * languageCount === 1 ⇒ 1.0; >= langFullReference ⇒ 0. Inactive when unknown.
 */
function localizationFactor(row) {
  if (Array.isArray(row.languageCodesISO2A) && row.languageCodesISO2A.length > 0) {
    return detectLocalizationGap(row) ? 1 : 0;
  }

  const lc = row.languageCount ?? null;
  if (lc === null || lc < 1) return null;
  const { langFullReference } = OPPORTUNITY_PARAMS;
  if (langFullReference <= 1) return 0;
  return clamp(1 - (lc - 1) / (langFullReference - 1), 0, 1);
}

/**
 * Growth momentum (增长动能): prefer the signed popularity delta vs the last
 * saved snapshot; fall back to update recency from releaseDate. Inactive when
 * neither a prior snapshot nor a parseable release date is available.
 */
function momentumFactor(row, nowMs) {
  const delta = row.popularityDelta;
  if (delta && typeof delta.value === 'number' && Number.isFinite(delta.value)) {
    const { momentumDeltaScale } = OPPORTUNITY_PARAMS;
    return clamp(0.5 + delta.value / (2 * momentumDeltaScale), 0, 1);
  }
  // Recency proxy: a recently-updated storefront listing signals active momentum.
  const days = daysSince(row.releaseDate, nowMs);
  if (days === null) return null;
  const { recencyWindowDays } = OPPORTUNITY_PARAMS;
  return clamp(1 - days / recencyWindowDays, 0, 1);
}

/**
 * Chart visibility (proxy for 竞品密度): the app's own best published chart
 * rank, #1 ⇒ 1.0, #100 ⇒ ~0. Inactive unless chart enrichment populated a rank.
 */
function visibilityFactor(row) {
  const rank = bestChartRank(row);
  if (rank === null || rank < 1 || rank > 100) return null;
  return clamp(1 - (rank - 1) / 99, 0, 1);
}

/**
 * Chart volatility (榜单波动): how much this app's standing has swung since the
 * last snapshot. Inactive until a prior snapshot delta exists.
 */
function volatilityFactor(row) {
  const delta = row.popularityDelta;
  if (!delta || typeof delta.value !== 'number' || !Number.isFinite(delta.value)) {
    return null;
  }
  const { volatilityDeltaScale } = OPPORTUNITY_PARAMS;
  return clamp(Math.abs(delta.value) / volatilityDeltaScale, 0, 1);
}

// ── Small helpers ─────────────────────────────────────────────────────────────

function maxRatingCountOf(rows) {
  return rows.reduce((max, r) => {
    if (!r || r.available === false || r.error) return max;
    const c = r.userRatingCount ?? 0;
    return c > max ? c : max;
  }, 0);
}

/**
 * Whole days between an ISO date (YYYY-MM-DD or full ISO) and `nowMs`.
 * Returns null when the date is missing/unparseable or `nowMs` is absent.
 * Future dates clamp to 0 days (treated as freshly updated).
 */
function daysSince(isoDate, nowMs) {
  if (typeof isoDate !== 'string' || !isoDate.trim()) return null;
  if (typeof nowMs !== 'number' || !Number.isFinite(nowMs)) return null;
  const then = Date.parse(isoDate);
  if (Number.isNaN(then)) return null;
  const days = (nowMs - then) / 86_400_000;
  return days < 0 ? 0 : days;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
